import { useEffect, useMemo, useState } from "react";
import { supabase, SUPABASE_URL, SUPABASE_ANON_KEY } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { UserPlus, Users, Trash2, Mail, CheckCircle, Clock, Activity, Loader2 } from "lucide-react";
import { getTodayInZambia, getZambiaDayBounds, formatInZambia } from "@/lib/zambia-time";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ActivityTimeline } from "@/components/ActivityTimeline";
import { UserAvatar } from "@/components/UserAvatar";
import { buildActivityEntries, getRangeQueryBounds, type ActivityEntry, type ActivityRange } from "@/lib/activity";

interface Invitation {
  id: string;
  email: string;
  status: string;
  created_at: string;
}

interface TeamMember {
  id: string;
  user_id: string;
  email: string | null;
  full_name: string | null;
  avatar_url?: string | null;
  created_at: string;
  status?: string;
  business_id?: string | null;
}

interface DailyActivityRow {
  user_id: string;
  full_name: string | null;
  email: string | null;
  business_id?: string | null;
  sales_today: number;
  sales_total_today: number;
  expenses_today: number;
}

export default function TeamManagementPage() {
  const { user, businessId, profile, isFounder, session } = useAuth();
  const { toast } = useToast();
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [dailyActivity, setDailyActivity] = useState<DailyActivityRow[]>([]);
  const [activityRange, setActivityRange] = useState<ActivityRange>("today");
  const [teamActivity, setTeamActivity] = useState<ActivityEntry[]>([]);
  const [loading, setLoading] = useState(true);
  /** Map: "business_id|email" -> inviter display name (who added this employee to the team) */
  const [addedByMap, setAddedByMap] = useState<Record<string, string>>({});
  /** Resolved business_id from profile refetch so invite works when needed */
  const [resolvedBusinessId, setResolvedBusinessId] = useState<string | null>(null);
  /** User id being removed (loading state for delete button) */
  const [removingUserId, setRemovingUserId] = useState<string | null>(null);
  /** Pending remove: show confirm dialog */
  const [removeConfirm, setRemoveConfirm] = useState<{ userId: string; label: string } | null>(null);

  const fetchData = async () => {
    if (!businessId) return;
    setLoading(true);

    const invQuery = supabase
      .from("team_invitations")
      .select("id, email, status, created_at")
      .order("created_at", { ascending: false })
      .eq("business_id", businessId);
    const memberQuery = supabase
      .from("profiles")
      .select("id, user_id, email, full_name, avatar_url, created_at, status, business_id")
      .order("created_at", { ascending: false })
      .eq("business_id", businessId);
    const acceptedInvQuery = supabase
      .from("team_invitations")
      .select("email, business_id, invited_by")
      .eq("status", "accepted")
      .eq("business_id", businessId);

    const [invRes, memberRes, acceptedRes] = await Promise.all([
      invQuery,
      memberQuery,
      acceptedInvQuery,
    ]);

    if (invRes.data) setInvitations(invRes.data);
    const memberList = (memberRes.data || []) as TeamMember[];
    if (memberRes.data) setMembers(memberList);

    // Resolve "added by" (owner/admin who invited each employee)
    const accepted = (acceptedRes.data || []) as { email: string; business_id: string; invited_by: string }[];
    const inviterIds = [...new Set(accepted.map((a) => a.invited_by))];
    const inviterProfiles: Record<string, string> = {};
    if (inviterIds.length > 0) {
      const { data: inviterRows } = await supabase
        .from("profiles")
        .select("user_id, full_name, email")
        .in("user_id", inviterIds);
      (inviterRows || []).forEach((r: { user_id: string; full_name: string | null; email: string | null }) => {
        inviterProfiles[r.user_id] = r.full_name?.trim() || r.email || "Unknown";
      });
    }
    const map: Record<string, string> = {};
    accepted.forEach((a) => {
      const key = `${a.business_id}|${(a.email || "").toLowerCase().trim()}`;
      map[key] = inviterProfiles[a.invited_by] ?? "—";
    });
    setAddedByMap(map);

    const today = getTodayInZambia();
    const { start: todayStartDate, end: todayEndDate } = getZambiaDayBounds(today);
    const todayStart = todayStartDate.toISOString();
    const todayEnd = todayEndDate.toISOString();

    const salesTodayQuery = supabase
      .from("sales")
      .select("sold_by, final_amount, total_price")
      .is("voided_at", null)
      .gte("created_at", todayStart)
      .lt("created_at", todayEnd);
    const expensesTodayQuery = supabase
      .from("expenses")
      .select("created_by")
      .eq("expense_date", today);

    const [salesRes, expRes] = await Promise.all([
      salesTodayQuery.eq("business_id", businessId),
      expensesTodayQuery.eq("business_id", businessId),
    ]);

    const salesByUser: Record<string, number> = {};
    const salesTotalByUser: Record<string, number> = {};
    (salesRes.data || []).forEach((r: { sold_by: string; final_amount: number | null; total_price: number | null }) => {
      salesByUser[r.sold_by] = (salesByUser[r.sold_by] || 0) + 1;
      salesTotalByUser[r.sold_by] =
        (salesTotalByUser[r.sold_by] || 0) + Number(r.final_amount ?? r.total_price ?? 0);
    });
    const expensesByUser: Record<string, number> = {};
    (expRes.data || []).forEach((r: { created_by: string }) => {
      expensesByUser[r.created_by] = (expensesByUser[r.created_by] || 0) + 1;
    });

    const activity: DailyActivityRow[] = (memberList || []).map((m: TeamMember) => ({
      user_id: m.user_id,
      full_name: m.full_name,
      email: m.email,
      business_id: m.business_id,
      sales_today: salesByUser[m.user_id] || 0,
      sales_total_today: salesTotalByUser[m.user_id] || 0,
      expenses_today: expensesByUser[m.user_id] || 0,
    }));
    setDailyActivity(activity);

    const activityBounds = getRangeQueryBounds(activityRange);
    const teamSalesQuery = supabase
      .from("sales")
      .select("id, item_name, quantity, final_amount, total_price, created_at, sold_by, voided_at")
      .is("voided_at", null)
      .gte("created_at", activityBounds.startIso)
      .lte("created_at", activityBounds.endIso)
      .order("created_at", { ascending: false });
    const teamExpensesQuery = supabase
      .from("expenses")
      .select("id, category, description, amount, expense_date, date, created_by")
      .gte("expense_date", activityBounds.startDate)
      .lte("expense_date", activityBounds.endDate)
      .order("date", { ascending: false });

    const [teamSalesRes, teamExpensesRes] = await Promise.all([
      teamSalesQuery.eq("business_id", businessId),
      teamExpensesQuery.eq("business_id", businessId),
    ]);

    const actorLabelById: Record<string, string> = {};
    memberList.forEach((member) => {
      actorLabelById[member.user_id] = member.full_name?.trim() || member.email || member.user_id.slice(0, 8);
    });

    setTeamActivity(
      buildActivityEntries(
        (teamSalesRes.data ?? []) as any[],
        (teamExpensesRes.data ?? []) as any[],
        actorLabelById,
      ),
    );
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, [businessId, activityRange]);

  const topPerformerToday = useMemo(() => {
    const ranked = dailyActivity
      .filter((row) => row.sales_today > 0 || row.sales_total_today > 0)
      .sort((a, b) => {
        if (b.sales_total_today !== a.sales_total_today) return b.sales_total_today - a.sales_total_today;
        return b.sales_today - a.sales_today;
      });
    return ranked[0] ?? null;
  }, [dailyActivity]);

  // Founder without businessId: resolve business_id from profile so Invite works
  useEffect(() => {
    if (!user?.id || (businessId ?? profile?.business_id ?? resolvedBusinessId)) return;
    supabase.from("profiles").select("business_id").eq("user_id", user.id).single().then(({ data }) => {
      if (data?.business_id) setResolvedBusinessId(data.business_id);
    });
  }, [user?.id, businessId, profile?.business_id, resolvedBusinessId]);

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedEmail = email.trim().toLowerCase();
    if (!trimmedEmail) return;
    if (!user) {
      toast({ title: "Not signed in", description: "Please sign in to invite team members.", variant: "destructive" });
      return;
    }
    let targetBusinessId = businessId ?? profile?.business_id ?? resolvedBusinessId ?? null;
    if (!targetBusinessId && isFounder && user.id) {
      const { data } = await supabase.from("profiles").select("business_id").eq("user_id", user.id).single();
      targetBusinessId = data?.business_id ?? null;
      if (targetBusinessId) setResolvedBusinessId(targetBusinessId);
    }
    if (!targetBusinessId) {
      toast({
        title: "Cannot invite yet",
        description: "Your account must be linked to a business. Use Retry Profile Sync or contact ReviewBoost Pro Manager support to link your profile.",
        variant: "destructive",
      });
      return;
    }

    // Basic email validation
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      toast({ title: "Invalid email", description: "Please enter a valid email address.", variant: "destructive" });
      return;
    }

    // Check for duplicate pending invitation
    const existing = invitations.find(
      (inv) => inv.email.toLowerCase() === trimmedEmail && inv.status === "pending"
    );
    if (existing) {
      toast({ title: "Already invited", description: "This email already has a pending invitation.", variant: "destructive" });
      return;
    }

    // Check if already a member
    const alreadyMember = members.find((m) => m.email?.toLowerCase() === trimmedEmail);
    if (alreadyMember) {
      toast({ title: "Already a member", description: "This email is already part of your team.", variant: "destructive" });
      return;
    }

    setSubmitting(true);
    const accessToken = session?.access_token;
    if (!accessToken) {
      toast({ title: "Session expired", description: "Please refresh the page and try again.", variant: "destructive" });
      setSubmitting(false);
      return;
    }
    try {
      const url = `${SUPABASE_URL}/functions/v1/invite-team-member`;
      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
          ...(SUPABASE_ANON_KEY ? { apikey: SUPABASE_ANON_KEY } : {}),
        },
        body: JSON.stringify({ email: trimmedEmail, business_id: targetBusinessId }),
      });
      const text = await res.text();
      let data: { ok?: boolean; error?: string; message?: string; msg?: string } = {};
      try {
        data = text ? (JSON.parse(text) as typeof data) : {};
      } catch {
        data = { error: text?.slice(0, 200) || `Request failed (${res.status})` };
      }
      const errMsg = data?.error ?? data?.msg;

      if (!res.ok) {
        toast({
          title: "Invitation failed",
          description: errMsg || res.statusText || `Request failed (${res.status})`,
          variant: "destructive",
        });
      } else if (data?.ok === false || errMsg) {
        toast({
          title: "Invitation failed",
          description: errMsg || "Something went wrong",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Invitation sent",
          description: data?.message || `${trimmedEmail} has been invited to your team.`,
        });
        setEmail("");
        fetchData();
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Network error";
      toast({
        title: "Invitation failed",
        description: msg,
        variant: "destructive",
      });
    }
    setSubmitting(false);
  };

  const handleDeleteInvitation = async (id: string) => {
    const { error } = await supabase.from("team_invitations").delete().eq("id", id);
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      setInvitations((prev) => prev.filter((inv) => inv.id !== id));
      toast({ title: "Invitation removed" });
    }
  };

  const handleRemoveEmployee = async (userId: string) => {
    setRemovingUserId(userId);
    setRemoveConfirm(null);
    const { error } = await supabase.rpc("remove_team_member", { p_user_id: userId });
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      await fetchData();
      toast({ title: "Employee removed", description: "They will lose access to your business." });
    }
    setRemovingUserId(null);
  };

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-display font-bold">Team Management</h2>
        <p className="text-sm text-muted-foreground">
          Invite employees and manage your team members.
        </p>
      </div>

      {/* Invite Form */}
      <Card className="border border-border max-w-lg">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <UserPlus className="h-4 w-4 text-primary" />
            Invite Employee
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleInvite} className="flex gap-3">
            <div className="flex-1 space-y-1">
              <Label className="sr-only">Email</Label>
              <Input
                type="email"
                placeholder="employee@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                maxLength={255}
              />
            </div>
            <Button type="submit" disabled={submitting}>
              {submitting ? "Sending..." : "Invite"}
            </Button>
          </form>
          <p className="text-xs text-muted-foreground mt-2">
            An email with a magic link will be sent to the invitee. They can click the link to sign up or sign in and will be added to your team automatically.
          </p>
        </CardContent>
      </Card>

      {/* Daily Activity — Admin: sales and expenses each user logged today */}
      <Card className="border border-border">
        <CardHeader>
          <CardTitle className="text-base">Top Performer Today</CardTitle>
          <p className="text-sm text-muted-foreground">
            Based on the highest personal sales value recorded today.
          </p>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-6">
              <div className="h-6 w-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : topPerformerToday ? (
            <div className="space-y-2">
              <div className="text-lg font-semibold">
                {topPerformerToday.full_name || topPerformerToday.email || topPerformerToday.user_id.slice(0, 8)}
              </div>
              {topPerformerToday.email && (
                <div className="text-sm text-muted-foreground">{topPerformerToday.email}</div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-2">
                <div className="rounded-md border border-border px-3 py-3">
                  <div className="text-xs text-muted-foreground">Sales Count</div>
                  <div className="text-xl font-bold">{topPerformerToday.sales_today}</div>
                </div>
                <div className="rounded-md border border-border px-3 py-3">
                  <div className="text-xs text-muted-foreground">Sales Total</div>
                  <div className="text-xl font-bold">K {topPerformerToday.sales_total_today.toLocaleString()}</div>
                </div>
                <div className="rounded-md border border-border px-3 py-3">
                  <div className="text-xs text-muted-foreground">Expenses Logged</div>
                  <div className="text-xl font-bold">{topPerformerToday.expenses_today}</div>
                </div>
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No employee sales recorded today yet.</p>
          )}
        </CardContent>
      </Card>

      <Card className="border border-border">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Activity className="h-4 w-4 text-primary" />
            Daily Activity
          </CardTitle>
          <p className="text-sm text-muted-foreground">How many sales and expenses each team member logged today</p>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-6">
              <div className="h-6 w-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : dailyActivity.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">No team members yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-2 px-2 font-semibold">User</th>
                    <th className="text-left py-2 px-2 font-semibold">Team</th>
                    <th className="text-right py-2 px-2 font-semibold">Sales today</th>
                    <th className="text-right py-2 px-2 font-semibold">Sales total today</th>
                    <th className="text-right py-2 px-2 font-semibold">Expenses today</th>
                  </tr>
                </thead>
                <tbody>
                  {dailyActivity.map((row) => {
                    const bid = row.business_id ?? businessId ?? "";
                    const key = `${bid}|${(row.email ?? "").toLowerCase().trim()}`;
                    const addedBy = addedByMap[key] ?? "—";
                    return (
                      <tr key={row.user_id} className="border-b border-border/40">
                        <td className="py-2 px-2">
                          <span className="font-medium">{row.full_name || row.email || row.user_id.slice(0, 8)}</span>
                          {row.email && <span className="block text-xs text-muted-foreground">{row.email}</span>}
                        </td>
                        <td className="py-2 px-2 text-muted-foreground" title="Owner/admin who added this member">
                          {addedBy}
                        </td>
                        <td className="py-2 px-2 text-right">{row.sales_today}</td>
                        <td className="py-2 px-2 text-right">K {row.sales_total_today.toLocaleString()}</td>
                        <td className="py-2 px-2 text-right">{row.expenses_today}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border border-border">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Activity className="h-4 w-4 text-primary" />
            Team Activity
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            A chronological view of what employees have logged. Admins can use this to audit individual activity.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <Tabs value={activityRange} onValueChange={(value) => setActivityRange(value as ActivityRange)}>
            <TabsList>
              <TabsTrigger value="today">Today</TabsTrigger>
              <TabsTrigger value="week">This Week</TabsTrigger>
              <TabsTrigger value="month">This Month</TabsTrigger>
            </TabsList>
          </Tabs>
          {loading ? (
            <div className="flex justify-center py-6">
              <div className="h-6 w-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <ActivityTimeline
              entries={teamActivity}
              emptyText="No employee activity in this range yet."
              showActor
            />
          )}
        </CardContent>
      </Card>

      {/* Pending Invitations */}
      <Card className="border border-border">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Mail className="h-4 w-4 text-primary" />
            Invitations
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-6">
              <div className="h-6 w-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : invitations.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">No invitations yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-2 px-2 font-semibold">Email</th>
                    <th className="text-left py-2 px-2 font-semibold">Status</th>
                    <th className="text-left py-2 px-2 font-semibold">Date</th>
                    <th className="py-2 px-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {invitations.map((inv) => (
                    <tr key={inv.id} className="border-b border-border/40">
                      <td className="py-2 px-2">{inv.email}</td>
                      <td className="py-2 px-2">
                        <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${
                          inv.status === "accepted"
                            ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                            : "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400"
                        }`}>
                          {inv.status === "accepted" ? <CheckCircle className="h-3 w-3" /> : <Clock className="h-3 w-3" />}
                          {inv.status}
                        </span>
                      </td>
                      <td className="py-2 px-2 text-muted-foreground">
                        {formatInZambia(inv.created_at)}
                      </td>
                      <td className="py-2 px-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() =>
                            inv.status === "accepted"
                              ? (() => {
                                  const m = members.find((x) => x.email?.toLowerCase() === inv.email?.toLowerCase());
                                  if (m) setRemoveConfirm({ userId: m.user_id, label: m.email || inv.email || "this person" });
                                  else handleDeleteInvitation(inv.id);
                                })()
                              : handleDeleteInvitation(inv.id)
                          }
                          disabled={removingUserId !== null}
                          className="h-7 w-7 text-muted-foreground hover:text-destructive"
                          title={inv.status === "accepted" ? "Remove employee" : "Cancel invitation"}
                        >
                          {removingUserId && members.some((x) => x.user_id === removingUserId && x.email?.toLowerCase() === inv.email?.toLowerCase()) ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <Trash2 className="h-3.5 w-3.5" />
                          )}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Current Members */}
      <Card className="border border-border">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Users className="h-4 w-4 text-primary" />
            Team Members
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-6">
              <div className="h-6 w-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : members.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">No team members yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-2 px-2 font-semibold">Name</th>
                    <th className="text-left py-2 px-2 font-semibold">Email</th>
                    <th className="text-left py-2 px-2 font-semibold">Added by</th>
                    <th className="text-left py-2 px-2 font-semibold">Status</th>
                    <th className="text-left py-2 px-2 font-semibold">Joined</th>
                    <th className="py-2 px-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {members.map((m) => {
                    const bid = m.business_id ?? businessId ?? "";
                    const key = `${bid}|${(m.email ?? "").toLowerCase().trim()}`;
                    const addedBy = addedByMap[key] ?? "—";
                    return (
                      <tr key={m.id} className="border-b border-border/40">
                        <td className="py-2 px-2">
                          <div className="flex items-center gap-2">
                            <UserAvatar size="sm" userId={m.user_id} avatarUrl={m.avatar_url} fullName={m.full_name} />
                            <span>{m.full_name || "—"}</span>
                          </div>
                        </td>
                        <td className="py-2 px-2">{m.email || "—"}</td>
                        <td className="py-2 px-2">
                          <span className="text-muted-foreground" title="Team owner/admin who added this member">
                            {addedBy}
                          </span>
                        </td>
                        <td className="py-2 px-2">
                          <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${
                            (m.status ?? "active") === "active"
                              ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                              : "bg-muted text-muted-foreground"
                          }`}>
                            {m.status ?? "active"}
                          </span>
                        </td>
                        <td className="py-2 px-2 text-muted-foreground">
                          {formatInZambia(m.created_at)}
                        </td>
                        <td className="py-2 px-2">
                          {m.user_id !== user?.id && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => setRemoveConfirm({ userId: m.user_id, label: m.full_name || m.email || "this person" })}
                              disabled={removingUserId !== null}
                              className="h-7 w-7 text-muted-foreground hover:text-destructive"
                              title="Remove employee"
                            >
                              {removingUserId === m.user_id ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              ) : (
                                <Trash2 className="h-3.5 w-3.5" />
                              )}
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={!!removeConfirm} onOpenChange={(open) => !open && setRemoveConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove employee?</AlertDialogTitle>
            <AlertDialogDescription>
              {removeConfirm
                ? `${removeConfirm.label} will lose access to your business. They can sign in again but will no longer see your inventory or team data.`
                : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => removeConfirm && handleRemoveEmployee(removeConfirm.userId)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
